import {
  closestCenter,
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { DragHandleDots2Icon } from '@radix-ui/react-icons';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Button } from '../../primitives/button';
import { Label } from '../../primitives/label';
import { LoadingAddon } from '../../primitives/loading-addon';
import { Switch } from '../../primitives/switch';
import { Redo2Icon } from '../../svg/redo-2-icon';
import { Undo2Icon } from '../../svg/undo-2-icon';
import { cn } from '../../utils/cn';
import { TableResponsiveWrapper } from '../table/table-responsive-wrapper';
import { LOGICAL_OPERATOR_LABELS } from './constants';
import { Group } from './group';
import {
  RuleBuilderDisabledProvider,
  RuleBuilderProvider,
  useRuleBuilderDisabled,
  useRuleBuilderStore,
  useRuleBuilderTemporalStore,
} from './store';
import type {
  TConditionsOutput,
  TFact,
  TGroupNode,
  TNode,
  TObjectPath,
  TRuleBuilderProps,
  TRuleBuilderRef,
} from './types';
import {
  findNodeById,
  isDescendantOf,
  isNodeVisibleInBuilder,
  normalizePathToStored,
} from './utils';

export type { TConditionsOutput, TFact, TRuleBuilderProps, TRuleBuilderRef };

// ─── Inner component (reads from store) ─────────────────────────────────────

type TInnerProps = {
  facts: TFact[];
  showConfirmationToggle?: boolean;
  showHistoryControls?: boolean;
  showFloatingRedo?: boolean;
  responsive?: boolean;
  isLoading?: boolean;
};

/**
 * RuleBuilderInner
 *
 * Internal component that connects to the Zustand store and orchestrates
 * the top-level drag-and-drop context via `@dnd-kit/core`.
 * This component is separated from the public `RuleBuilder` so it can access
 * the store provided by `RuleBuilderProvider` without being the provider itself.
 */
function RuleBuilderInner(
  {
    facts,
    showConfirmationToggle = false,
    showHistoryControls = false,
    showFloatingRedo = false,
    responsive = true,
    isLoading = false,
  }: TInnerProps,
  ref: React.ForwardedRef<TRuleBuilderRef>,
) {
  const [customFacts, setCustomFacts] = useState<TFact[]>([]);
  const [objectPathsByFact, setObjectPathsByFact] = useState<Record<string, TObjectPath[]>>({});
  const hasHydratedInitialCustomFactsRef = useRef(false);
  const hasHydratedInitialObjectPathsRef = useRef(false);

  const disabled = useRuleBuilderDisabled();
  const rootGroup = useRuleBuilderStore((s) => s.root);
  const triggerValidation = useRuleBuilderStore((s) => s.triggerValidation);
  const moveNode = useRuleBuilderStore((s) => s.moveNode);
  const reorderGroup = useRuleBuilderStore((s) => s.reorderGroup);
  const undo = useRuleBuilderStore((s) => s.undo);
  const redo = useRuleBuilderStore((s) => s.redo);
  const canUndo = useRuleBuilderTemporalStore((s) => s.pastStates.length > 0);
  const canRedo = useRuleBuilderTemporalStore((s) => s.futureStates.length > 0);
  const showValidation = useRuleBuilderStore((s) => s.validation.showValidation);
  const invalidNodeIds = useRuleBuilderStore((s) => s.validation.invalidNodeIds);
  const kind = useRuleBuilderStore((s) => s.kind);

  const mergedFacts = useMemo(() => {
    const factsMap = new Map<string, TFact>();
    facts.forEach((fact) => {
      factsMap.set(fact.attributeName, fact);
    });
    customFacts.forEach((fact) => {
      if (!factsMap.has(fact.attributeName)) {
        factsMap.set(fact.attributeName, fact);
      }
    });
    return Array.from(factsMap.values());
  }, [customFacts, facts]);

  const factAttributeNames = useMemo(
    () => new Set(mergedFacts.map((f) => f.attributeName)),
    [mergedFacts],
  );

  const customFactNames = useMemo(
    () => new Set(customFacts.map((fact) => fact.attributeName)),
    [customFacts],
  );

  const usedFactNames = useMemo(() => {
    const usedFacts = new Set<string>();

    const collectUsedFacts = (node: TNode) => {
      if (node.type === 'condition') {
        const factName = node.fact.trim();
        if (factName) {
          usedFacts.add(factName);
        }
        return;
      }

      node.children.forEach(collectUsedFacts);
    };

    collectUsedFacts(rootGroup);
    return usedFacts;
  }, [rootGroup]);

  const handleCreateCustomFact = useCallback((customFact: TFact) => {
    setCustomFacts((prevCustomFacts) => {
      if (prevCustomFacts.some((fact) => fact.attributeName === customFact.attributeName)) {
        return prevCustomFacts;
      }
      return [...prevCustomFacts, customFact];
    });
  }, []);

  const handleDeleteCustomFact = useCallback(
    (factName: string) => {
      setCustomFacts((prevCustomFacts) => {
        if (usedFactNames.has(factName)) {
          return prevCustomFacts;
        }
        return prevCustomFacts.filter((fact) => fact.attributeName !== factName);
      });
    },
    [usedFactNames],
  );

  const usedObjectPathsByFact = useMemo(() => {
    const usedPaths: Record<string, Set<string>> = {};

    const collectUsedPaths = (node: TNode) => {
      if (node.type === 'condition') {
        const factName = node.fact.trim();
        const factType = node.factType?.toLowerCase().trim();
        const normalizedPath = normalizePathToStored(node.path ?? '');
        if (factName && factType === 'object' && normalizedPath) {
          usedPaths[factName] ??= new Set<string>();
          usedPaths[factName].add(normalizedPath);
        }
        return;
      }

      node.children.forEach(collectUsedPaths);
    };

    collectUsedPaths(rootGroup);

    return usedPaths;
  }, [rootGroup]);

  const handleCreateObjectPath = useCallback((factName: string, objectPath: TObjectPath) => {
    if (!factName) return;

    setObjectPathsByFact((prev) => {
      const currentPaths = prev[factName] ?? [];
      if (currentPaths.some((entry) => entry.path === objectPath.path)) {
        return prev;
      }

      return {
        ...prev,
        [factName]: [...currentPaths, objectPath],
      };
    });
  }, []);

  const handleDeleteObjectPath = useCallback(
    (factName: string, path: string) => {
      if (!factName || !path) return;
      if (usedObjectPathsByFact[factName]?.has(path)) return;

      setObjectPathsByFact((prev) => {
        const currentPaths = prev[factName] ?? [];
        const nextPaths = currentPaths.filter((entry) => entry.path !== path);

        if (nextPaths.length === currentPaths.length) return prev;

        if (nextPaths.length === 0) {
          const { [factName]: _removed, ...rest } = prev;
          return rest;
        }

        return {
          ...prev,
          [factName]: nextPaths,
        };
      });
    },
    [usedObjectPathsByFact],
  );

  useEffect(() => {
    if (hasHydratedInitialCustomFactsRef.current) return;

    const originalFacts = new Set(facts.map((fact) => fact.attributeName.trim()));
    const missingFacts = new Map<string, TFact>();

    const collectMissingFacts = (node: TNode) => {
      if (node.type === 'condition') {
        const factName = node.fact.trim();
        if (!factName || originalFacts.has(factName) || missingFacts.has(factName)) {
          return;
        }

        missingFacts.set(factName, {
          attributeName: factName,
          codeDataType: node.factType?.trim() ?? '',
          description: 'Fact personalizado',
          allowedValues: undefined,
        });
        return;
      }

      node.children.forEach(collectMissingFacts);
    };

    collectMissingFacts(rootGroup);

    if (missingFacts.size > 0) {
      setCustomFacts((prevCustomFacts) => {
        const nextFacts = [...prevCustomFacts];
        missingFacts.forEach((missingFact) => {
          if (!nextFacts.some((fact) => fact.attributeName === missingFact.attributeName)) {
            nextFacts.push(missingFact);
          }
        });
        return nextFacts;
      });
    }

    hasHydratedInitialCustomFactsRef.current = true;
  }, [facts, rootGroup]);

  useEffect(() => {
    if (hasHydratedInitialObjectPathsRef.current) return;

    const nextPathsByFact: Record<string, TObjectPath[]> = {};

    const collectInitialObjectPaths = (node: TNode) => {
      if (node.type === 'condition') {
        const factName = node.fact.trim();
        const factType = node.factType?.toLowerCase().trim();
        const pathType = node.pathType?.toLowerCase().trim();

        if (
          factName &&
          factType === 'object' &&
          normalizePathToStored(node.path ?? '') &&
          pathType &&
          pathType !== 'object'
        ) {
          const normalizedPath = normalizePathToStored(node.path ?? '');
          nextPathsByFact[factName] ??= [];

          if (!nextPathsByFact[factName].some((entry) => entry.path === normalizedPath)) {
            nextPathsByFact[factName].push({
              path: normalizedPath,
              pathType,
            });
          }
        }

        return;
      }

      node.children.forEach(collectInitialObjectPaths);
    };

    collectInitialObjectPaths(rootGroup);

    if (Object.keys(nextPathsByFact).length > 0) {
      setObjectPathsByFact((prev) => {
        const next = { ...prev };

        Object.entries(nextPathsByFact).forEach(([factName, entries]) => {
          const current = next[factName] ?? [];
          const merged = [...current];

          entries.forEach((entry) => {
            if (!merged.some((item) => item.path === entry.path)) {
              merged.push(entry);
            }
          });

          if (merged.length > 0) {
            next[factName] = merged;
          }
        });

        return next;
      });
    }

    hasHydratedInitialObjectPathsRef.current = true;
  }, [rootGroup]);

  const hasObsoleteFacts = useMemo(() => {
    function check(node: TNode): boolean {
      if (!isNodeVisibleInBuilder(node, kind)) return false;
      if (node.type === 'condition') return !!node.fact && !factAttributeNames.has(node.fact);
      return node.children.some(check);
    }
    return rootGroup.children.some(check);
  }, [factAttributeNames, kind, rootGroup]);

  const hasInvalidNodes = showValidation && invalidNodeIds.size > 0;

  const [draggingNodeId, setDraggingNodeId] = useState<string | null>(null);
  const [requireConfirmation, setRequireConfirmation] = useState(true);
  const ruleBuilderViewportRef = useRef<HTMLDivElement | null>(null);
  const historyControlsSentinelRef = useRef<HTMLDivElement | null>(null);
  const [showFloatingHistoryControls, setShowFloatingHistoryControls] = useState(false);
  const [isRuleBuilderInView, setIsRuleBuilderInView] = useState(true);
  const [isHistoryActionHighlightActive, setIsHistoryActionHighlightActive] = useState(false);
  const historyActionHighlightTimeoutRef = useRef<number | null>(null);

  useImperativeHandle(ref, () => ({
    isValid: () => triggerValidation(),
  }));

  useEffect(() => {
    if (!showHistoryControls) {
      setShowFloatingHistoryControls(false);
      return;
    }

    const sentinel = historyControlsSentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowFloatingHistoryControls(!entry.isIntersecting);
      },
      { threshold: 0 },
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [showHistoryControls]);

  useEffect(() => {
    const ruleBuilder = ruleBuilderViewportRef.current;
    if (!ruleBuilder || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsRuleBuilderInView(entry.isIntersecting);
      },
      { threshold: 0 },
    );

    observer.observe(ruleBuilder);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    return () => {
      if (historyActionHighlightTimeoutRef.current !== null) {
        window.clearTimeout(historyActionHighlightTimeoutRef.current);
      }
    };
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  /**
   * Custom collision detection that strictly respects each group's visual border.
   *
   * Key insight: when a nested Group A sits inside Root, dnd-kit registers THREE
   * droppables that spatially overlap the Group A area:
   *   • "drop-root"   — Root's children container (covers the whole root area)
   *   • "drop-groupA" — Group A's children container (only the inner bordered area)
   *   • "groupA"      — Group A as a *sortable item* inside Root's SortableContext
   *
   * The old approach picked "groupA" (the sortable) via closestCenter, which caused
   * Root to highlight even though the pointer was physically inside Group A's border.
   *
   * Strategy:
   * 1. Keyboard DnD has no pointer coords → fall back to closestCenter.
   * 2. Use pointerWithin to find every droppable whose rect contains the pointer.
   * 3. Keep only the "drop-*" containers. If none → pointer is outside all borders → no collision.
   * 4. From those containers, pick the innermost one (smallest rect area = deepest nesting).
   * 5. Among all pointerWithin hits, keep only *sortable children* whose parentGroupId
   *    matches the innermost group's ID. This filters out items belonging to outer groups.
   * 6. If such children exist → closestCenter among them (determines insert position).
   * 7. Otherwise → target the group container itself (handles empty groups and gaps).
   */
  const collisionDetection = useCallback<CollisionDetection>((args) => {
    // Keyboard sensors don't provide pointer coordinates – keep original behaviour.
    if (!args.pointerCoordinates) return closestCenter(args);

    const withinCollisions = pointerWithin(args);

    // Only consider group-container droppables (the ones with a visual border).
    const containerCollisions = withinCollisions.filter(({ id }) => String(id).startsWith('drop-'));

    // Pointer is outside every group's border → no drop target.
    if (containerCollisions.length === 0) return [];

    // Find the innermost group container (smallest area = most deeply nested).
    const innermostContainer = containerCollisions.reduce((best, candidate) => {
      const bestRect = args.droppableRects.get(best.id);
      const candRect = args.droppableRects.get(candidate.id);
      if (!bestRect || !candRect) return best;
      return candRect.width * candRect.height < bestRect.width * bestRect.height ? candidate : best;
    });

    const innermostGroupId = String(innermostContainer.id).replace(/^drop-/, '');

    // Find sortable items that are direct children of the innermost group AND
    // whose rect contains the pointer. Excludes items from outer groups.
    const childSortables = withinCollisions.filter(({ id }) => {
      if (String(id).startsWith('drop-')) return false;
      const droppable = args.droppableContainers.find((c) => c.id === id);
      const data = droppable?.data?.current as { parentGroupId?: string } | undefined;
      return data?.parentGroupId === innermostGroupId;
    });

    if (childSortables.length > 0) {
      // Pointer is over a direct child → closestCenter only among those children.
      return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter((c) =>
          childSortables.some((col) => col.id === c.id),
        ),
      });
    }

    // Pointer is in the group's empty space or padding → target the group container.
    return [innermostContainer];
  }, []);

  const handleDragStart = useCallback(({ active }: DragStartEvent) => {
    setDraggingNodeId(String(active.id));
  }, []);

  const handleDragCancel = useCallback(() => {
    setDraggingNodeId(null);
  }, []);

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      setDraggingNodeId(null);
      if (!over) return;

      const activeNodeId = String(active.id);
      const overNodeId = String(over.id);
      if (activeNodeId === overNodeId) return;

      const dragSourceData = active.data.current as
        { parentGroupId?: string; nodeType?: string } | undefined;
      const dropTargetData = over.data.current as
        { parentGroupId?: string; nodeType?: string; groupId?: string } | undefined;

      // Dropped onto a droppable group container (e.g., empty group droppable area)
      if (dropTargetData?.groupId) {
        // Guard: never drop a node into itself or one of its own descendants
        if (dropTargetData.groupId === activeNodeId) return;
        const targetGroup = findNodeById(rootGroup, dropTargetData.groupId) as TGroupNode | null;
        if (targetGroup && !isDescendantOf(rootGroup, activeNodeId, dropTargetData.groupId)) {
          moveNode(activeNodeId, dropTargetData.groupId, targetGroup.children.length);
        }
        return;
      }

      const sourceGroupId = dragSourceData?.parentGroupId;
      const targetGroupId = dropTargetData?.parentGroupId;
      if (!sourceGroupId || !targetGroupId) return;

      const targetGroup = findNodeById(rootGroup, targetGroupId) as TGroupNode | null;
      if (!targetGroup) return;

      const targetChildIndex = targetGroup.children.findIndex((child) => child.id === overNodeId);
      const insertIndex = targetChildIndex >= 0 ? targetChildIndex : targetGroup.children.length;

      if (sourceGroupId === targetGroupId) {
        const sourceGroup = findNodeById(rootGroup, sourceGroupId) as TGroupNode | null;
        if (!sourceGroup) return;
        const originalIndex = sourceGroup.children.findIndex((child) => child.id === activeNodeId);
        if (originalIndex !== insertIndex) {
          reorderGroup(sourceGroupId, originalIndex, insertIndex);
        }
      } else {
        moveNode(activeNodeId, targetGroupId, insertIndex);
      }
    },
    [rootGroup, moveNode, reorderGroup],
  );

  const draggingNode = useMemo(
    () => (draggingNodeId ? findNodeById(rootGroup, draggingNodeId) : null),
    [draggingNodeId, rootGroup],
  );

  const renderHistoryControls = useCallback(
    (wrapperClassName?: string) => (
      <div className={cn('flex gap-2', wrapperClassName)}>
        <Button
          aria-label="Deshacer"
          size="icon"
          title="Deshacer"
          variant="secondary"
          disabled={!canUndo || disabled}
          onClick={() => {
            undo();
            setIsHistoryActionHighlightActive(true);
            if (historyActionHighlightTimeoutRef.current !== null) {
              window.clearTimeout(historyActionHighlightTimeoutRef.current);
            }
            historyActionHighlightTimeoutRef.current = window.setTimeout(() => {
              setIsHistoryActionHighlightActive(false);
              historyActionHighlightTimeoutRef.current = null;
            }, 600);
          }}
        >
          <Undo2Icon />
        </Button>
        <Button
          aria-label="Rehacer"
          size="icon"
          title="Rehacer"
          variant="secondary"
          disabled={!canRedo || disabled}
          onClick={() => {
            redo();
            setIsHistoryActionHighlightActive(true);
            if (historyActionHighlightTimeoutRef.current !== null) {
              window.clearTimeout(historyActionHighlightTimeoutRef.current);
            }
            historyActionHighlightTimeoutRef.current = window.setTimeout(() => {
              setIsHistoryActionHighlightActive(false);
              historyActionHighlightTimeoutRef.current = null;
            }, 600);
          }}
        >
          <Redo2Icon />
        </Button>
      </div>
    ),
    [canRedo, canUndo, disabled, redo, undo],
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="grid gap-4">
        <div className="relative flex items-center justify-end">
          {showHistoryControls && <div ref={historyControlsSentinelRef} className="size-px" />}
          {showHistoryControls && renderHistoryControls()}
          {isLoading && <LoadingAddon type="plain" />}
        </div>

        {showFloatingRedo &&
          showHistoryControls &&
          showFloatingHistoryControls &&
          isRuleBuilderInView &&
          renderHistoryControls(
            'fixed top-[4rem] right-2 z-50 border border-input rounded-full bg-background/5 p-2 px-[0.6rem] shadow-lg backdrop-blur-sm supports-[backdrop-filter]:bg-background/5',
          )}

        {responsive ? (
          <TableResponsiveWrapper>
            <div
              ref={ruleBuilderViewportRef}
              className={cn('rounded-3xl border p-2 shadow-xs', {
                'border-input': !isHistoryActionHighlightActive,
                'border-accent': isHistoryActionHighlightActive,
              })}
            >
              <Group
                node={rootGroup}
                facts={mergedFacts}
                isRoot
                customFactNames={customFactNames}
                usedFactNames={usedFactNames}
                onDeleteCustomFact={handleDeleteCustomFact}
                objectPathsByFact={objectPathsByFact}
                usedObjectPathsByFact={usedObjectPathsByFact}
                onCreateObjectPath={handleCreateObjectPath}
                onDeleteObjectPath={handleDeleteObjectPath}
                requireConfirmation={requireConfirmation}
                onCreateCustomFact={handleCreateCustomFact}
              />
            </div>
          </TableResponsiveWrapper>
        ) : (
          <div
            ref={ruleBuilderViewportRef}
            className={cn('rounded-3xl border p-2 shadow-xs', {
              'border-input': !isHistoryActionHighlightActive,
              'border-accent': isHistoryActionHighlightActive,
            })}
          >
            <Group
              node={rootGroup}
              facts={mergedFacts}
              isRoot
              customFactNames={customFactNames}
              usedFactNames={usedFactNames}
              onDeleteCustomFact={handleDeleteCustomFact}
              objectPathsByFact={objectPathsByFact}
              usedObjectPathsByFact={usedObjectPathsByFact}
              onCreateObjectPath={handleCreateObjectPath}
              onDeleteObjectPath={handleDeleteObjectPath}
              requireConfirmation={requireConfirmation}
              onCreateCustomFact={handleCreateCustomFact}
            />
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {hasInvalidNodes ? (
              <span className="text-xs font-medium text-destructive">Regla con errores</span>
            ) : hasObsoleteFacts ? (
              <span className="text-xs font-medium text-warning">
                Regla con facts obsoletos o personalizados
              </span>
            ) : null}
          </div>
          {showConfirmationToggle && (
            <div className="flex items-center gap-2">
              <Label
                htmlFor="double-confirm-switch"
                className="cursor-pointer text-xs text-muted-foreground"
              >
                Confirmar antes de eliminar
              </Label>
              <Switch
                id="double-confirm-switch"
                checked={requireConfirmation}
                disabled={disabled}
                onCheckedChange={setRequireConfirmation}
              />
            </div>
          )}
        </div>
      </div>
      <DragOverlay>
        {draggingNode?.type === 'condition' ? (
          <div className="flex min-h-[6rem] items-center gap-2 rounded-lg border border-input bg-background/50 p-2 opacity-95 shadow-xl">
            <div className="flex size-9 items-center justify-center rounded-full text-muted-foreground">
              <DragHandleDots2Icon className="size-4" />
            </div>
            <span className="text-sm">
              {draggingNode.fact ? `Condición para: ${draggingNode.fact}` : 'Condición'}
            </span>
          </div>
        ) : draggingNode?.type === 'group' ? (
          <div className="flex min-h-24 opacity-95">
            <div className="flex w-full overflow-hidden rounded-lg rounded-tr-none border border-input bg-background/50 shadow-xl">
              <div
                className={cn('flex w-6 items-center justify-center', {
                  'bg-indigo-300 text-indigo-950': draggingNode.operator === 'all',
                  'bg-amber-300 text-amber-950': draggingNode.operator === 'any',
                  'bg-rose-300 text-rose-950': draggingNode.operator === 'not',
                })}
              >
                <span className="-rotate-90 text-xs font-semibold whitespace-nowrap">
                  {LOGICAL_OPERATOR_LABELS[draggingNode.operator]}
                </span>
              </div>

              <div className="flex items-center px-3">
                <span className="text-sm">Grupo</span>
              </div>
            </div>
            <div className="flex size-9 items-center justify-center rounded-r-lg border-t border-r border-b border-input text-muted-foreground">
              <DragHandleDots2Icon className="size-4" />
            </div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

const RuleBuilderInnerWithRef = forwardRef(RuleBuilderInner);

// ─── Public component ────────────────────────────────────────────────────────

/**
 * RuleBuilder
 *
 * Visual rule editor that produces a `json-rules-engine`-compatible `conditions` object.
 *
 * Internally the component builds an immutable tree of `TGroupNode` and `TConditionNode`
 * nodes, serialized to/from the `TConditionsOutput` JSON format on every change.
 *
 * @example
 * // Basic usage
 * <RuleBuilder facts={facts} onChange={(v) => console.log(v)} />
 *
 * @example
 * // With initial value
 * <RuleBuilder facts={facts} defaultValue={savedConditions} onChange={setValue} />

 * @example
 * // Lock the root group operator
 * <RuleBuilder facts={facts} fixedRootOperator="all" onChange={setValue} />
 *
 * @example
 * // With ref for imperative validation
 * const ref = useRef<TRuleBuilderRef>(null);
 * const handleSubmit = () => {
 *   const { isRuleValid } = ref.current!.isValid();
 * };
 * <RuleBuilder ref={ref} facts={facts} onChange={setValue} />
 *
 * @example
 * // Inside react-hook-form Controller
 * <Controller
 *   control={control}
 *   name="conditions"
 *   render={({ field }) => (
 *     <RuleBuilder facts={facts} defaultValue={field.value} onChange={field.onChange} />
 *   )}
 * />
 *
 * @example
 * // To reset the editor externally, change the key prop
 * <RuleBuilder key={ruleId} facts={facts} defaultValue={savedConditions} onChange={setValue} />
 */
export const RuleBuilder = forwardRef<TRuleBuilderRef, TRuleBuilderProps>(function RuleBuilder(
  {
    defaultValue,
    disabled = false,
    isLoading = false,
    facts = [],
    fixedRootOperator,
    kind = 'rule',
    onChange,
    responsive = true,
    showConfirmationToggle = false,
    showHistoryControls = false,
    showFloatingRedo = false,
  },
  ref,
) {
  const innerRef = useRef<TRuleBuilderRef>(null);

  useImperativeHandle(ref, () => ({
    isValid: () => innerRef.current?.isValid() ?? { isRuleValid: false },
  }));

  return (
    <RuleBuilderDisabledProvider value={disabled}>
      <RuleBuilderProvider
        defaultValue={defaultValue}
        fixedRootOperator={fixedRootOperator}
        kind={kind}
        onChange={onChange}
      >
        <RuleBuilderInnerWithRef
          facts={facts}
          ref={innerRef}
          responsive={responsive}
          showConfirmationToggle={showConfirmationToggle}
          isLoading={isLoading}
          showHistoryControls={showHistoryControls}
          showFloatingRedo={showFloatingRedo}
        />
      </RuleBuilderProvider>
    </RuleBuilderDisabledProvider>
  );
});
